/**
 * InvoiceFormController — page /facture-edition.html (EPIC-013 / 13.7,
 * EPIC-053 / 53.5).
 *
 * Deux modes :
 *  - Édition (`?id=<uuid>`) : charge un draft via GET /invoices/{id},
 *    auto-save toutes les 8s via PATCH /invoices/{id} (uniquement si
 *    `status === 'draft'`).
 *  - Création (sans id) : champs vides. Au premier auto-save / sauvegarde
 *    manuelle, POST /invoices/draft pour créer le brouillon, puis
 *    bascule en mode édition (history.replaceState).
 *
 * Verrouillage : si la facture n'est plus en `draft`, désactive tous les
 * inputs et affiche un bandeau d'alerte (la facture est lue, pas modifiée).
 *
 * Émission : déclenche POST /invoices/{id}/issue. 422 mention légale
 * manquante → message inline avec lien profil entreprise.
 *
 * Programmation (EPIC-053 / 53.5) :
 *  - draft → scheduled via POST /invoices/{id}/schedule
 *    ({toIssueOn}) ; bornes J+1 ≤ d ≤ dernier jour du mois suivant.
 *  - scheduled → draft via POST /invoices/{id}/unschedule.
 *  - En statut `scheduled`, form verrouillé, sidebar bascule sur la card
 *    « Émission programmée » qui propose l'annulation.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";
import { formatDate } from "/js/utils/format.js";

const AUTOSAVE_DELAY_MS = 8000;

export default class InvoiceFormController extends BaseController {
  static targets = [
    "crumbNumber",
    "overline",
    "title",
    "statusPill",
    "statusLabel",
    "autosaveLabel",
    "lockedBanner",
    "legalBanner",
    "formFieldset",
    "clientSelect",
    "clientHint",
    "clientError",
    "referenceInput",
    "dateEmissionDisplay",
    "dateEmissionInput",
    "dateDueDisplay",
    "dateDueInput",
    "dateDueHint",
    "conditionsSelect",
    "linesContainer",
    "addLineBtn",
    "discountEnabled",
    "discountFields",
    "discountPercentInput",
    "discountLabelInput",
    "notesTextarea",
    "attachmentsList",
    "subtotalHt",
    "discountRow",
    "discountRowPercent",
    "discountRowLabel",
    "discountRowAmount",
    "totalHtAfterDiscount",
    "totalVat",
    "totalTtc",
    "vatStatusCard",
    "vatStatusText",
    "saveDraftBtn",
    "issueBtn",
    "issueBtnWrapper",
    "cancelBtn",
    "previewBtn",
    "deleteBtn",
    "scheduleCard",
    "toIssueOnInput",
    "scheduleBtn",
    "scheduleError",
    "scheduledInfoCard",
    "scheduledDateLabel",
    "scheduledBodyText",
    "unscheduleBtn",
    "standardMode",
    "milestoneCreatePanel",
    "milestoneCreateQuoteNumber",
    "milestoneCreateLabel",
    "milestoneCreateModeAmount",
    "milestoneCreateModePercent",
    "milestoneCreateAmountField",
    "milestoneCreateAmount",
    "milestoneCreatePercentField",
    "milestoneCreatePercent",
    "milestoneCreateError",
    "milestoneCreateSubmit",
    "milestoneCreateCancel",
  ];

  connect() {
    const params = new URLSearchParams(window.location.search);
    this._invoiceId = params.get("id");
    this._quoteId = params.get("quoteId");
    this._mode = (this._quoteId && params.get("kind") === "milestone")
      ? "milestone-create"
      : "standard";
    this._autosaveTimer = null;
    this._lines = [];
    this._lineSeq = 0;
    this._documents = [];
    this._isLocked = false;
    this._currentStatus = "draft";

    if (this._mode === "milestone-create") {
      this._enterMilestoneCreateMode();
      return;
    }
    this._loadInitialData();
  }

  disconnect() {
    if (this._autosaveTimer) clearTimeout(this._autosaveTimer);
  }

  async _loadInitialData() {
    try {
      const [clientsResp, documentsResp] = await Promise.all([
        api.listClients({ "filter[status]": "active", perPage: 100 }),
        api.listDocuments().catch(() => ({ documents: [] })),
      ]);
      this._populateClientSelect(Array.isArray(clientsResp?.data) ? clientsResp.data : []);
      this._documents = Array.isArray(documentsResp?.documents) ? documentsResp.documents : [];
      this._renderAttachments([]);

      if (this._invoiceId) {
        const invoice = await api.getInvoice(this._invoiceId);
        // Brouillon de solde non éditable (recalculé à la volée) : on bascule
        // vers la fiche détail. EPIC-099 / 99.49 — D6 : protège l'accès direct
        // via URL `/facture-edition.html?id=<settlement>`.
        if (invoice?.kind === "settlement") {
          window.location.replace(`/facture-detail.html?id=${encodeURIComponent(this._invoiceId)}`);
          return;
        }
        this._hydrate(invoice);
      } else {
        // Mode création : initial empty + 1 ligne vide.
        this._addEmptyLine();
        this._setDefaultDates();
        this._recomputeTotals();
        this._renderStatus("draft", null);
        this._renderScheduleSection(null);
        this._renderLockedState();
      }
    } catch (error) {
      this._showApiError(error, "Impossible de charger les données du formulaire.");
    }
  }

  // ---------------------------------------------------------------------------
  // Population & hydratation
  // ---------------------------------------------------------------------------

  _populateClientSelect(clients) {
    if (!this.hasClientSelectTarget) return;
    const sel = this.clientSelectTarget;
    while (sel.firstChild) sel.removeChild(sel.firstChild);
    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = "— Sélectionner un client —";
    sel.appendChild(placeholder);
    for (const client of clients) {
      const opt = document.createElement("option");
      opt.value = client.id;
      opt.textContent = client.legalName ?? client.id;
      sel.appendChild(opt);
    }
    const sep = document.createElement("option");
    sep.disabled = true;
    sep.textContent = "──────────";
    sel.appendChild(sep);
    const newClient = document.createElement("option");
    newClient.value = "__new__";
    newClient.textContent = "+ Nouveau client…";
    sel.appendChild(newClient);
  }

  _hydrate(invoice) {
    this._invoiceId = invoice.id;
    this._currentStatus = invoice.status;
    this._isLocked = invoice.status !== "draft";

    if (this.hasCrumbNumberTarget) {
      this.crumbNumberTarget.textContent = invoice.number ?? "Brouillon";
    }
    if (this.hasReferenceInputTarget) {
      this.referenceInputTarget.value = invoice.number ?? "(auto-numéroté à l'émission)";
    }
    if (this.hasClientSelectTarget && invoice.clientId) {
      this.clientSelectTarget.value = invoice.clientId;
    }
    this._renderDatesEditMode(invoice);
    if (this.hasNotesTextareaTarget) {
      this.notesTextareaTarget.value = invoice.notes ?? "";
    }

    if (this.hasDiscountEnabledTarget) {
      const hasDiscount = !!(invoice.discountPercent && Number(invoice.discountPercent) > 0);
      this.discountEnabledTarget.checked = hasDiscount;
      this._toggleDiscountFields(hasDiscount);
      if (hasDiscount && this.hasDiscountPercentInputTarget) {
        this.discountPercentInputTarget.value = invoice.discountPercent;
      }
      if (hasDiscount && this.hasDiscountLabelInputTarget) {
        this.discountLabelInputTarget.value = invoice.discountLabel ?? "";
      }
    }

    this._lines = [];
    this._lineSeq = 0;
    this._clearLinesContainer();
    const sourceLines = Array.isArray(invoice.lines) ? invoice.lines : [];
    for (const line of sourceLines) {
      this._appendLine({
        label: line.label ?? "",
        reference: line.reference ?? "",
        qty: line.quantity ?? "1",
        unitPrice: line.priceExclVat ?? line.unitPriceExclVat ?? "0",
        vatRate: line.vatRate ?? "20",
        articleId: line.articleId ?? null,
        activityId: line.activityId ?? null,
      });
    }
    if (sourceLines.length === 0) this._addEmptyLine();

    this._renderAttachments(invoice.attachedDocumentIds ?? []);
    this._renderStatus(invoice.status, invoice);
    this._renderScheduleSection(invoice);
    this._renderLockedState();
    this._recomputeTotals();
  }

  // ---------------------------------------------------------------------------
  // Programmation d'émission (EPIC-053 / 53.5)
  // ---------------------------------------------------------------------------

  _renderScheduleSection(invoice) {
    this._setScheduleInputBounds();
    if (invoice?.status !== "scheduled") return;
    const dateStr = this._formatHumanDate(invoice.toIssueOn);
    if (this.hasScheduledDateLabelTarget) {
      this.scheduledDateLabelTarget.textContent = dateStr;
    }
    if (this.hasScheduledBodyTextTarget) {
      const clientType = invoice.client?.type ?? invoice.clientType;
      const isB2B = clientType === "b2b" || clientType === "public_administration";
      this.scheduledBodyTextTarget.textContent = isB2B
        ? `Cette facture sera transmise à la Plateforme Agréée du destinataire le ${dateStr}.`
        : `Cette facture sera envoyée par email à votre client le ${dateStr}.`;
    }
  }

  _setScheduleInputBounds() {
    if (!this.hasToIssueOnInputTarget) return;
    const { min, max } = this._scheduleBounds();
    this.toIssueOnInputTarget.min = this._toIsoDate(min);
    this.toIssueOnInputTarget.max = this._toIsoDate(max);
  }

  async schedule(event) {
    event?.preventDefault();
    if (!this._invoiceId) {
      this._showScheduleError("Enregistrez d'abord le brouillon avant de programmer l'émission.");
      return;
    }
    const value = this.hasToIssueOnInputTarget ? this.toIssueOnInputTarget.value : "";
    if (!this._validateScheduleDate(value)) return;
    if (this._autosaveTimer) clearTimeout(this._autosaveTimer);
    await this._performSave(true);
    const formatted = this._formatHumanDate(value);
    const confirmed = await this.confirm(
      `Programmer l'émission au ${formatted} ? Vous ne pourrez plus modifier la facture après confirmation.`,
    );
    if (!confirmed) return;
    try {
      await api.scheduleInvoice(this._invoiceId, value);
      this.showSuccess(`Facture programmée pour le ${formatted}.`);
      window.location.reload();
    } catch (error) {
      if (error instanceof APIError && error.status === 422) {
        this._showScheduleError(error.message || "Date d'émission invalide.");
        return;
      }
      this._showApiError(error, "Impossible de programmer l'émission.");
    }
  }

  async unschedule(event) {
    event?.preventDefault();
    if (!this._invoiceId) return;
    const confirmed = await this.confirm(
      "Annuler la programmation ? La facture redeviendra modifiable en brouillon.",
    );
    if (!confirmed) return;
    try {
      await api.unscheduleInvoice(this._invoiceId);
      this.showSuccess("Programmation annulée.");
      window.location.reload();
    } catch (error) {
      this._showApiError(error, "Impossible d'annuler la programmation.");
    }
  }

  _validateScheduleDate(value) {
    if (!value) {
      this._showScheduleError("Sélectionnez une date d'émission programmée.");
      return false;
    }
    const target = new Date(`${value}T00:00:00`);
    if (!Number.isFinite(target.getTime())) {
      this._showScheduleError("Date invalide.");
      return false;
    }
    const { min, max } = this._scheduleBounds();
    if (target < min) {
      this._showScheduleError("La date doit être au plus tôt demain.");
      return false;
    }
    if (target > max) {
      this._showScheduleError("La date doit être au plus tard le dernier jour du mois suivant.");
      return false;
    }
    this._clearScheduleError();
    return true;
  }

  _scheduleBounds() {
    const now = new Date();
    const min = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const max = new Date(now.getFullYear(), now.getMonth() + 2, 0);
    return { min, max };
  }

  _showScheduleError(message) {
    if (!this.hasScheduleErrorTarget) return;
    this.scheduleErrorTarget.textContent = message;
    this.scheduleErrorTarget.hidden = false;
  }

  _clearScheduleError() {
    if (!this.hasScheduleErrorTarget) return;
    this.scheduleErrorTarget.textContent = "";
    this.scheduleErrorTarget.hidden = true;
  }

  _formatHumanDate(isoDate) {
    if (!isoDate) return "—";
    const d = new Date(`${isoDate}T00:00:00`);
    if (!Number.isFinite(d.getTime())) return isoDate;
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
  }

  _toIsoDate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  _renderStatus(status, _invoice) {
    if (this.hasStatusLabelTarget) {
      const map = {
        draft: "brouillon",
        scheduled: "programmée",
        issued: "émise",
        sent: "envoyée",
        paid: "payée",
        partially_paid: "partiellement payée",
        overdue: "en retard",
        cancelled: "annulée",
        archived: "archivée",
      };
      this.statusLabelTarget.textContent = map[status] ?? status;
    }
    if (this.hasStatusPillTarget) {
      const map = {
        draft: "nm-pill--indigo",
        scheduled: "nm-pill--blue",
        issued: "nm-pill--ochre",
        sent: "nm-pill--ochre",
        paid: "nm-pill--olive",
        partially_paid: "nm-pill--blue",
        overdue: "nm-pill--red",
        cancelled: "",
        archived: "",
      };
      this.statusPillTarget.className = "nm-pill";
      const mod = map[status];
      if (mod) this.statusPillTarget.classList.add(mod);
    }
  }

  _renderLockedState() {
    const isDraft = this._currentStatus === "draft";
    const isScheduled = this._currentStatus === "scheduled";

    if (this.hasLockedBannerTarget) {
      // Bandeau verrouillage : caché en draft (édition normale) et en
      // scheduled (la card sidebar « Émission programmée » prend le relais
      // et propose l'annulation).
      this.lockedBannerTarget.hidden = isDraft || isScheduled;
    }
    if (this.hasFormFieldsetTarget) {
      this.formFieldsetTarget.disabled = !isDraft;
    }
    if (this.hasAutosaveLabelTarget) {
      this.autosaveLabelTarget.hidden = !isDraft;
    }
    // Header — boutons d'action sur le brouillon (draft seul).
    if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.hidden = !isDraft;
    if (this.hasCancelBtnTarget) this.cancelBtnTarget.hidden = !isDraft;
    if (this.hasDeleteBtnTarget) this.deleteBtnTarget.hidden = !isDraft;
    // Bouton primaire « Émettre et envoyer » dans la card Totaux — draft seul.
    if (this.hasIssueBtnWrapperTarget) this.issueBtnWrapperTarget.hidden = !isDraft;
    // Card sidebar « Émettre automatiquement » — draft seul.
    if (this.hasScheduleCardTarget) this.scheduleCardTarget.hidden = !isDraft;
    // Card sidebar « Émission programmée » — scheduled seul.
    if (this.hasScheduledInfoCardTarget) this.scheduledInfoCardTarget.hidden = !isScheduled;
  }

  /**
   * Mode création : aperçu des dates en lecture seule. Émission = aujourd'hui,
   * échéance = aujourd'hui + 30 j (valeurs définitives posées par le backend à
   * l'enregistrement / l'émission). L'échéance ne devient éditable qu'ensuite.
   * Les inputs (cachés) portent la valeur ISO envoyée dans le payload.
   */
  _setDefaultDates() {
    const today = new Date();
    const todayIso = today.toISOString().slice(0, 10);
    const due = new Date(today);
    due.setDate(due.getDate() + 30);
    const dueIso = due.toISOString().slice(0, 10);

    if (this.hasDateEmissionInputTarget && !this.dateEmissionInputTarget.value) {
      this.dateEmissionInputTarget.value = todayIso;
    }
    if (this.hasDateEmissionDisplayTarget) {
      this.dateEmissionDisplayTarget.textContent = formatDate(todayIso);
    }

    if (this.hasDateDueInputTarget && !this.dateDueInputTarget.value) {
      this.dateDueInputTarget.value = dueIso;
    }
    if (this.hasDateDueInputTarget) this.dateDueInputTarget.hidden = true;
    if (this.hasDateDueDisplayTarget) {
      this.dateDueDisplayTarget.textContent = formatDate(dueIso);
      this.dateDueDisplayTarget.hidden = false;
    }
    if (this.hasDateDueHintTarget) {
      this.dateDueHintTarget.textContent =
        "La date d'échéance sera modifiable après l'enregistrement du brouillon.";
    }
  }

  /**
   * Mode modification (la facture a un id) : la date d'émission s'affiche en
   * texte (non éditable, fixée à l'émission), l'échéance devient un champ
   * éditable pré-rempli. L'input émission (caché) reste porteur de la valeur.
   */
  _renderDatesEditMode(invoice) {
    if (this.hasDateEmissionInputTarget) {
      this.dateEmissionInputTarget.value = invoice.dateEmission ?? "";
    }
    if (this.hasDateEmissionDisplayTarget) {
      this.dateEmissionDisplayTarget.textContent = formatDate(invoice.dateEmission, { emptyValue: "—" });
    }
    if (this.hasDateDueDisplayTarget) this.dateDueDisplayTarget.hidden = true;
    if (this.hasDateDueInputTarget) {
      this.dateDueInputTarget.hidden = false;
      this.dateDueInputTarget.value = invoice.dateDue ?? "";
    }
    if (this.hasDateDueHintTarget) {
      this.dateDueHintTarget.textContent = "Paiement à 30 jours par défaut.";
    }
  }

  _renderAttachments(selectedIds) {
    if (!this.hasAttachmentsListTarget) return;
    const list = this.attachmentsListTarget;
    while (list.firstChild) list.removeChild(list.firstChild);
    const selectedSet = new Set(selectedIds ?? []);
    for (const doc of this._documents) {
      const fragment = this.cloneTemplate("tpl-attachment-item");
      if (!fragment.firstElementChild) continue;
      this.fillSlots(fragment, { name: doc.name ?? doc.id });
      const checkbox = fragment.querySelector('input[type="checkbox"]');
      if (checkbox) {
        checkbox.value = doc.id;
        checkbox.id = `att-${doc.id}`;
        checkbox.checked = selectedSet.has(doc.id);
      }
      list.appendChild(fragment);
    }
  }

  // ---------------------------------------------------------------------------
  // Lignes dynamiques
  // ---------------------------------------------------------------------------

  addLine() {
    if (this._isLocked) return;
    this._addEmptyLine();
    this._scheduleAutosave();
  }

  removeLine(event) {
    if (this._isLocked) return;
    const row = event.currentTarget.closest("[data-line-id]");
    if (!row) return;
    const lineId = row.dataset.lineId;
    this._lines = this._lines.filter((l) => l.id !== lineId);
    row.remove();
    if (this._lines.length === 0) this._addEmptyLine();
    this._recomputeTotals();
    this._scheduleAutosave();
  }

  updateLine(event) {
    if (this._isLocked) return;
    const row = event.currentTarget.closest("[data-line-id]");
    if (!row) return;
    const lineId = row.dataset.lineId;
    const line = this._lines.find((l) => l.id === lineId);
    if (!line) return;
    const inputs = row.querySelectorAll("[data-line-target]");
    for (const input of inputs) {
      const key = input.dataset.lineTarget;
      if (key) line[key] = input.value;
    }
    // Recalcule le total HT de la ligne et l'affiche.
    const total = this._lineTotal(line);
    const totalInput = row.querySelector('[data-line-target="totalHt"]');
    if (totalInput) totalInput.value = this._formatNumber(total);
    this._recomputeTotals();
    this._scheduleAutosave();
  }

  _addEmptyLine() {
    this._appendLine({
      label: "",
      reference: "",
      qty: "1",
      unitPrice: "0",
      vatRate: "20",
      articleId: null,
      activityId: null,
    });
  }

  _appendLine(data) {
    const id = `l-${++this._lineSeq}`;
    const line = { id, ...data };
    this._lines.push(line);
    if (!this.hasLinesContainerTarget) return;
    const fragment = this.cloneTemplate("tpl-invoice-line-row");
    const root = fragment.firstElementChild;
    if (root) root.dataset.lineId = id;
    for (const input of fragment.querySelectorAll("[data-line-target]")) {
      const key = input.dataset.lineTarget;
      if (key && line[key] !== undefined && line[key] !== null) input.value = line[key];
    }
    const totalInput = fragment.querySelector('[data-line-target="totalHt"]');
    if (totalInput) totalInput.value = this._formatNumber(this._lineTotal(line));
    this.linesContainerTarget.appendChild(fragment);
  }

  _clearLinesContainer() {
    if (!this.hasLinesContainerTarget) return;
    // Garde le head (.nm-lines__head) et le footer (.nm-lines__add) si présents.
    const rows = this.linesContainerTarget.querySelectorAll("[data-line-id]");
    for (const row of rows) row.remove();
  }

  _lineTotal(line) {
    const qty = Number(String(line.qty ?? "").replace(",", "."));
    const price = Number(String(line.unitPrice ?? "").replace(",", "."));
    if (!Number.isFinite(qty) || !Number.isFinite(price)) return 0;
    return qty * price;
  }

  // ---------------------------------------------------------------------------
  // Totaux
  // ---------------------------------------------------------------------------

  toggleDiscount(event) {
    const enabled = event.currentTarget.checked;
    this._toggleDiscountFields(enabled);
    this._recomputeTotals();
    this._scheduleAutosave();
  }

  _toggleDiscountFields(enabled) {
    if (this.hasDiscountFieldsTarget) this.discountFieldsTarget.hidden = !enabled;
  }

  _recomputeTotals() {
    let subtotal = 0;
    let vatAmount = 0;
    for (const line of this._lines) {
      const lineHt = this._lineTotal(line);
      subtotal += lineHt;
      const rate = Number(String(line.vatRate ?? "0").replace(",", "."));
      if (Number.isFinite(rate)) vatAmount += lineHt * rate / 100;
    }

    let discountAmount = 0;
    const discountEnabled = this.hasDiscountEnabledTarget && this.discountEnabledTarget.checked;
    const discountPercent = discountEnabled && this.hasDiscountPercentInputTarget
      ? Number(String(this.discountPercentInputTarget.value ?? "0").replace(",", "."))
      : 0;
    if (discountEnabled && Number.isFinite(discountPercent) && discountPercent > 0) {
      discountAmount = subtotal * discountPercent / 100;
      vatAmount = vatAmount * (1 - discountPercent / 100);
    }
    const htAfterDiscount = subtotal - discountAmount;
    const totalTtc = htAfterDiscount + vatAmount;

    if (this.hasSubtotalHtTarget) this.subtotalHtTarget.textContent = this._formatAmount(subtotal);
    if (this.hasTotalHtAfterDiscountTarget) {
      this.totalHtAfterDiscountTarget.textContent = this._formatAmount(htAfterDiscount);
    }
    if (this.hasTotalVatTarget) this.totalVatTarget.textContent = this._formatAmount(vatAmount);
    if (this.hasTotalTtcTarget) this.totalTtcTarget.textContent = this._formatAmount(totalTtc);

    if (this.hasDiscountRowTarget) {
      this.discountRowTarget.hidden = !discountEnabled || discountAmount <= 0;
      if (this.hasDiscountRowPercentTarget) {
        this.discountRowPercentTarget.textContent = discountPercent
          ? `${discountPercent}%`
          : "";
      }
      if (this.hasDiscountRowLabelTarget) {
        const label = this.hasDiscountLabelInputTarget ? this.discountLabelInputTarget.value : "";
        this.discountRowLabelTarget.textContent = label ?? "";
      }
      if (this.hasDiscountRowAmountTarget) {
        this.discountRowAmountTarget.textContent = `− ${this._formatAmount(discountAmount)}`;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Auto-save & actions
  // ---------------------------------------------------------------------------

  scheduleAutosave() {
    this._scheduleAutosave();
  }

  _scheduleAutosave() {
    if (this._isLocked) return;
    if (this._autosaveTimer) clearTimeout(this._autosaveTimer);
    this._autosaveTimer = setTimeout(() => this._performSave(true), AUTOSAVE_DELAY_MS);
    if (this.hasAutosaveLabelTarget) {
      this.autosaveLabelTarget.textContent = "Modifications en attente…";
    }
  }

  async _performSave(silent = false) {
    if (this._isLocked) return;
    const payload = this._buildPayload();
    if (!payload.clientId) {
      if (!silent) this.showError("Sélectionnez un client avant d'enregistrer.");
      return;
    }
    try {
      const wasCreate = !this._invoiceId;
      const invoice = this._invoiceId
        ? await api.updateInvoiceDraft(this._invoiceId, payload)
        : await api.createInvoiceDraft(payload);
      this._invoiceId = invoice.id;
      this._currentStatus = invoice.status;
      this._isLocked = invoice.status !== "draft";
      if (wasCreate) {
        // Le brouillon existe désormais : on passe en mode modification (date
        // d'émission figée en texte, échéance éditable).
        this._renderDatesEditMode(invoice);
      }
      if (this.hasAutosaveLabelTarget) {
        this.autosaveLabelTarget.textContent = "Brouillon enregistré.";
      }
      if (!this._invoiceIdInUrl()) {
        const url = `${window.location.pathname}?id=${encodeURIComponent(invoice.id)}`;
        window.history.replaceState(null, "", url);
      }
      this._renderStatus(invoice.status, invoice);
      this._renderLockedState();
      if (!silent) this.showSuccess("Brouillon enregistré.");
    } catch (error) {
      if (this.hasAutosaveLabelTarget) {
        this.autosaveLabelTarget.textContent = "Échec de l'enregistrement.";
      }
      if (!silent) this._showApiError(error, "Impossible d'enregistrer le brouillon.");
    }
  }

  _invoiceIdInUrl() {
    return new URLSearchParams(window.location.search).has("id");
  }

  saveDraft(event) {
    event?.preventDefault();
    if (this._autosaveTimer) clearTimeout(this._autosaveTimer);
    this._performSave(false);
  }

  async issueAndSend(event) {
    event?.preventDefault();
    if (this._isLocked) return;
    await this._performSave(true);
    if (!this._invoiceId) return;
    const confirmed = await this.confirm(
      "Émettre cette facture ? Une fois émise, elle ne pourra plus être modifiée.",
    );
    if (!confirmed) return;
    try {
      const inv = await api.issueInvoice(this._invoiceId);
      this.showSuccess(`Facture émise : ${inv.number}.`);
      window.location.assign(`/facture-detail.html?id=${encodeURIComponent(inv.id)}`);
    } catch (error) {
      this._showApiError(error, "Impossible d'émettre la facture.");
    }
  }

  cancelEdit(event) {
    event?.preventDefault();
    window.location.assign("/factures.html");
  }

  previewPdf(event) {
    event?.preventDefault();
    if (!this._invoiceId) {
      this.showError("Enregistrez le brouillon avant d'afficher l'aperçu PDF.");
      return;
    }
    window.open(api.invoicePdfUrl(this._invoiceId), "_blank", "noopener");
  }

  async archiveDraft(event) {
    event?.preventDefault();
    if (!this._invoiceId) {
      window.location.assign("/factures.html");
      return;
    }
    const confirmed = await this.confirm(
      "Supprimer ce brouillon ? Il sera archivé (non visible dans la liste).",
    );
    if (!confirmed) return;
    try {
      await api.archiveInvoice(this._invoiceId);
      window.location.assign("/factures.html");
    } catch (error) {
      this._showApiError(error, "Impossible d'archiver le brouillon.");
    }
  }

  // ---------------------------------------------------------------------------
  // Payload
  // ---------------------------------------------------------------------------

  _buildPayload() {
    const payload = {
      clientId: this.hasClientSelectTarget && this.clientSelectTarget.value !== "__new__"
        ? this.clientSelectTarget.value
        : null,
      dateEmission: this.hasDateEmissionInputTarget ? this.dateEmissionInputTarget.value : null,
      dateDue: this.hasDateDueInputTarget ? this.dateDueInputTarget.value : null,
      notes: this.hasNotesTextareaTarget ? this.notesTextareaTarget.value : null,
      lines: this._lines
        .filter((l) => (l.label ?? "").trim() !== "")
        .map((l) => ({
          label: l.label ?? "",
          reference: l.reference || null,
          quantity: l.qty ?? "1",
          unitPriceExclVat: String(l.unitPrice ?? "0").replace(",", "."),
          vatRate: l.vatRate ?? "20",
          articleId: l.articleId ?? null,
          activityId: l.activityId ?? null,
        })),
    };
    if (this.hasDiscountEnabledTarget && this.discountEnabledTarget.checked) {
      payload.discountPercent = this.hasDiscountPercentInputTarget
        ? String(this.discountPercentInputTarget.value ?? "0").replace(",", ".")
        : "0";
      payload.discountLabel = this.hasDiscountLabelInputTarget
        ? this.discountLabelInputTarget.value
        : null;
    } else {
      payload.discountPercent = "0";
      payload.discountLabel = null;
    }
    if (this.hasAttachmentsListTarget) {
      const ids = [];
      for (const cb of this.attachmentsListTarget.querySelectorAll('input[type="checkbox"]:checked')) {
        if (cb.value) ids.push(cb.value);
      }
      payload.attachedDocumentIds = ids;
    }
    return payload;
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _formatAmount(value) {
    const num = Number(value);
    if (!Number.isFinite(num)) return "—";
    return num.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
  }

  _formatNumber(value) {
    const num = Number(value);
    if (!Number.isFinite(num)) return "0,00";
    return num.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  _showApiError(error, fallback) {
    const msg = error instanceof APIError && error.message ? error.message : fallback;
    this.showError(msg);
  }

  // ---------------------------------------------------------------------------
  // Mode « ajout post-émission d'une facture de situation » (EPIC-051 / 51.5)
  // Déclenché par ?quoteId=<uuid>&kind=milestone. Bypass le chargement standard.
  // ---------------------------------------------------------------------------

  async _enterMilestoneCreateMode() {
    if (this.hasStandardModeTarget) this.standardModeTarget.hidden = true;
    if (this.hasMilestoneCreatePanelTarget) this.milestoneCreatePanelTarget.hidden = false;
    if (this.hasMilestoneCreateCancelTarget && this._quoteId) {
      this.milestoneCreateCancelTarget.setAttribute(
        "href",
        `/devis-detail.html?id=${encodeURIComponent(this._quoteId)}`,
      );
    }
    try {
      const quote = await api.getQuote(this._quoteId);
      this._milestoneCreateQuote = quote;
      if (this.hasMilestoneCreateQuoteNumberTarget) {
        this.milestoneCreateQuoteNumberTarget.textContent = quote.number ?? "Brouillon";
      }
    } catch (error) {
      const msg = (error instanceof APIError && error.message)
        ? error.message
        : "Devis introuvable.";
      this._milestoneCreateFieldError(msg);
    }
  }

  onMilestoneCreateModeChange() {
    const percent = this.hasMilestoneCreateModePercentTarget && this.milestoneCreateModePercentTarget.checked;
    if (this.hasMilestoneCreateAmountFieldTarget) this.milestoneCreateAmountFieldTarget.hidden = percent;
    if (this.hasMilestoneCreatePercentFieldTarget) this.milestoneCreatePercentFieldTarget.hidden = !percent;
  }

  async submitMilestoneCreate() {
    if (this.hasMilestoneCreateErrorTarget) this.milestoneCreateErrorTarget.hidden = true;
    const label = (this.milestoneCreateLabelTarget?.value || "").trim();
    if (!label) {
      this._milestoneCreateFieldError("Saisissez un libellé.");
      return;
    }
    const isPercent = this.hasMilestoneCreateModePercentTarget && this.milestoneCreateModePercentTarget.checked;
    const payload = { label };
    if (isPercent) {
      const v = Number(this.milestoneCreatePercentTarget?.value);
      if (!Number.isFinite(v) || v <= 0 || v > 100) {
        this._milestoneCreateFieldError("Saisissez un pourcentage entre 0,01 et 100.");
        return;
      }
      payload.percent = v;
    } else {
      const v = Number(this.milestoneCreateAmountTarget?.value);
      if (!Number.isFinite(v) || v <= 0) {
        this._milestoneCreateFieldError("Saisissez un montant strictement positif.");
        return;
      }
      payload.amount = v;
    }
    if (this.hasMilestoneCreateSubmitTarget) this.milestoneCreateSubmitTarget.disabled = true;
    try {
      await api.addQuoteMilestone(this._quoteId, payload);
      this.showSuccess("Brouillon de jalon créé.");
      window.location.assign(`/devis-detail.html?id=${encodeURIComponent(this._quoteId)}`);
    } catch (error) {
      const code = error instanceof APIError ? error.data?.error?.code : null;
      let msg = (error instanceof APIError && error.message)
        ? error.message
        : "Erreur lors de la création du jalon.";
      if (code === "quote_overflow" || code === "invoice_overflow") {
        msg = "Ce montant porterait le total facturé au-delà du devis. Réduisez le montant ou émettez un avoir avant.";
      } else if (code === "settlement_already_issued") {
        msg = "La facture de solde a déjà été émise — aucun jalon supplémentaire n'est possible.";
      }
      this._milestoneCreateFieldError(msg);
      if (this.hasMilestoneCreateSubmitTarget) this.milestoneCreateSubmitTarget.disabled = false;
    }
  }

  _milestoneCreateFieldError(msg) {
    if (!this.hasMilestoneCreateErrorTarget) return;
    this.milestoneCreateErrorTarget.textContent = msg;
    this.milestoneCreateErrorTarget.hidden = false;
  }
}
